import { useEffect, useState } from 'react';
import { Modal, Form, Button, Message, Icon, Input } from 'semantic-ui-react';

// Firebase (modular v9+)
import {
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
} from 'firebase/auth';
import { useAuth } from '../../App';

export default function ChangePasswordDialog({
    open,
    onClose,
    onSuccess,
}) {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState(null);
    const { user } = useAuth();


    useEffect(() => {
        if (!open) {
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setError('');
            setInfo(null);
            setLoading(false);
            setShowPasswords(false);
        }
    }, [open]);

    const handleChangePassword = async (e) => {
        e?.preventDefault?.();
        setError('');
        setInfo(null);

        if (!user || !user.email) {
            setError('Nie ste prihlásený.');
            return;
        }

        if (newPassword.length < 8) {
            setError('Heslo musí mať aspoň 8 znakov.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Nové heslá sa nezhodujú.');
            return;
        }

        setLoading(true);

        try {
            // Reautentifikácia s aktuálnym heslom
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user.auth.currentUser, credential);

            // Aktualizácia hesla
            await updatePassword(user.auth.currentUser, newPassword);

            setInfo('Heslo bolo úspešne zmenené!');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            onSuccess?.();
        } catch (err) {
            console.error('Change password error:', err);
            switch (err.code) {
                case 'auth/wrong-password':
                    setError('Aktuálne heslo nie je správne.');
                    break;
                case 'auth/requires-recent-login':
                    setError('Pre zmenu hesla sa musíte znovu prihlásiť.');
                    break;
                case 'auth/weak-password':
                    setError('Heslo je príliš slabé.');
                    break;
                default:
                    setError('Nepodarilo sa zmeniť heslo. Skúste to znova.');
            }
        } finally {
            setLoading(false);
        }
    };

    const canSubmit =
        currentPassword.length > 0 &&
        newPassword.length >= 8 &&
        newPassword === confirmPassword &&
        !loading;

    return (
        <Modal open={open} onClose={onClose} size="tiny" closeOnDimmerClick={true}>
            <Modal.Header>Zmena hesla</Modal.Header>
            <Modal.Content>
                {!info && <Form onSubmit={handleChangePassword} noValidate>
                    <Form.Field required>
                        <label>Aktuálne heslo</label>
                        <Input
                            type={showPasswords ? 'text' : 'password'}
                            iconPosition="left"
                            placeholder="Zadajte aktuálne heslo"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            disabled={loading}
                            icon={<Icon name="lock" />}
                            action={{
                                icon: (
                                    <Icon name={showPasswords ? 'eye slash' : 'eye'} />
                                ),
                                type: 'button',
                                onClick: () => setShowPasswords((v) => !v),
                                title: showPasswords ? 'Skryť heslá' : 'Zobraziť heslá',
                            }}
                        />
                    </Form.Field>

                    <Form.Field required>
                        <label>Nové heslo</label>
                        <Input
                            type={showPasswords ? 'text' : 'password'}
                            iconPosition="left"
                            placeholder="Zadajte nové heslo"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={loading}
                            icon={<Icon name="lock" />}
                        />
                    </Form.Field>

                    <Form.Field required>
                        <label>Potvrdenie hesla</label>
                        <Input
                            type={showPasswords ? 'text' : 'password'}
                            iconPosition="left"
                            placeholder="Zopakujte nové heslo"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                            icon={<Icon name="lock" />}
                        />
                    </Form.Field>

                    {error && (
                        <Message negative size="small">
                            <Message.Content>
                                <Icon name="warning circle" /> {error}
                            </Message.Content>
                        </Message>
                    )}

                    <Button
                        type="submit"
                        color="yellow"
                        fluid
                        size="large"
                        loading={loading}
                        disabled={!canSubmit}
                        content="Zmeniť heslo"
                    />

                    <Button
                        type="button"
                        basic
                        fluid
                        size="large"
                        disabled={loading}
                        onClick={onClose}
                        style={{ marginTop: 8 }}
                        content="Zrušiť"
                    />
                </Form>}
                {info && (
                    <Form>
                        <Message positive size="small">
                            <Message.Content>
                                <Icon name="check circle" /> {info}
                            </Message.Content>
                        </Message>
                        <Button
                            type="button"
                            basic
                            fluid
                            size="large"
                            disabled={loading}
                            onClick={onClose}
                            style={{ marginTop: 8 }}
                            content="Zavrieť"
                        />
                    </Form>

                )}
            </Modal.Content>

            <style>{`
        @media (max-width: 480px) {
          .ui.modal > .content { padding-top: 1rem; padding-bottom: 1rem; }
          .ui.modal .ui.form .field > label { margin-bottom: .4rem; }
        }
        .ui.dimmer { background-color: rgba(0, 0, 0, 0.5);}
      `}</style>
        </Modal>
    );
}
